/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import React, { FC, Fragment, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Button,
  Divider,
  Popover,
  Radio,
  RadioChangeEvent,
} from "@duxton-web/ui"
import {
  ChevronTopUniversal,
  ChevronDownUniversal,
  PlaceholderFilled,
  TickMarkFilled,
  SubtractTiny,
  AddTiny,
} from "@duxton-web/icons"

import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import {
  MaxAPIStoreMerchant,
  StoreOpenStatusType,
} from "order-manager/types/models/store"
import {
  StoreStatus,
  StoreStatusForEndpointParam,
  durationAdjustment,
  storeCloseReasons,
  UnknownStoreStatus,
  EventCategory,
  EventAction,
  EventCategoryEng,
  EventActionEng,
} from "constants/tracking"

import { UpdateStoreStatusParams } from "order-manager/types/api/orderStoreOpenStatus.type"
import { formatPauseEndingTime } from "order-manager/utils/time_helper"
import { useLocaleContext, useTranslationsMap } from "common/utils/i18n"
import { AUTO_ACCEPT_GROUP, SPECIAL_MERCHANT_TYPE } from "constants/merchant"
import { TroyWebTracker } from "utils/tracking"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { useUpdateStoreStatusMutation } from "queries/orderManagerApi"
import { useLazyFetchStoreOpenStatusQuery } from "queries/merchantStoreApi"
import { actions } from "order-manager/store/duck/merchantstore"
import { getActionSuccess, getActionFail } from "store/duck/utils/actionUtils"
import {
  modalTitle,
  statusText,
  statusSection,
  statusSubText,
  statusMiddleSection,
  dividerStyle,
  confirmButton,
  greenButton,
  orangeButton,
  redButton,
  vermilionButton,
  buttonBase,
  customPopoverStyles,
  radioGroup,
  radioButton,
  timePickerContent,
  timePickerButton,
  timePickerText,
  tickMarkIcon,
  dotWrapper,
} from "./storeStatusChangeStyles"

type buttonStyleType = {
  [key: string]: any
}

const buttonStyle: buttonStyleType = {
  normal: greenButton,
  busy: orangeButton,
  paused: redButton,
  closed: vermilionButton,
}

const STORE_STATUS_POLL_INTERVAL_MS = 60000

const formatBusyModeMinutes = (
  minutesTemplate: string,
  busyModeDuration: number,
): string => {
  const fallback = `${busyModeDuration} min`
  if (!minutesTemplate || !minutesTemplate.includes("<x>")) {
    return fallback
  }
  return minutesTemplate.replace("<x>", String(busyModeDuration))
}

const TimePicker: FC<{
  setBusyModeDuration: (minutes: number) => void
  busyModeDuration: number
  busyModeConfig: MaxAPIStoreMerchant["busyModeConfig"]
  minutesTemplate: string
}> = ({
  setBusyModeDuration,
  busyModeDuration,
  busyModeConfig,
  minutesTemplate,
}) => {
  const [maxLimit, setMaxLimit] = useState(
    busyModeConfig ? busyModeConfig.busyModeFptUpperBound : null,
  )
  const [minLimit, setMinLimit] = useState(
    busyModeConfig ? busyModeConfig.busyModeFptLowerBound : null,
  )
  useEffect(() => {
    if (busyModeConfig) {
      setMinLimit(busyModeConfig.busyModeFptLowerBound)
      setMaxLimit(busyModeConfig.busyModeFptUpperBound)
    }
  }, [busyModeConfig])
  const onIncrease = () => {
    if (maxLimit) {
      const newDuration = Math.min(
        maxLimit,
        busyModeDuration + durationAdjustment,
      )
      setBusyModeDuration(newDuration)
    }
  }
  const onDecrease = () => {
    if (minLimit) {
      const newDuration = Math.max(
        minLimit,
        busyModeDuration - durationAdjustment,
      )
      setBusyModeDuration(newDuration)
    }
  }
  return (
    <div css={timePickerContent}>
      <div
        className={busyModeDuration === minLimit ? "disabled" : "enabled"}
        onClick={onDecrease}
        css={timePickerButton}
      >
        <SubtractTiny />
      </div>
      <span css={timePickerText}>
        {formatBusyModeMinutes(minutesTemplate, busyModeDuration)}
      </span>
      <div
        className={busyModeDuration === maxLimit ? "disabled" : "enabled"}
        onClick={onIncrease}
        css={timePickerButton}
      >
        <AddTiny />
      </div>
    </div>
  )
}

const computeServerStoreStatus = (storeOpenStatus: StoreOpenStatusType) => {
  if (!storeOpenStatus) return UnknownStoreStatus

  if (storeOpenStatus.isOpen) {
    return storeOpenStatus.isMexInBusyMode
      ? StoreStatus.busy
      : StoreStatus.normal
  }

  switch (storeOpenStatus.closeReason) {
    case storeCloseReasons.MexPaused:
    case storeCloseReasons.OpsPaused:
    case storeCloseReasons.OpsPausedWithoutComm:
      return StoreStatus.paused
    case storeCloseReasons.InActive:
    case storeCloseReasons.OutOfOpeningHours:
    case storeCloseReasons.OutOfSpecialOpeningHours:
    case storeCloseReasons.Restricted:
    case storeCloseReasons.Suspended:
      return StoreStatus.closed
    default:
      return UnknownStoreStatus
  }
}

const shouldShowChangeStoreStatusButton = (
  storeOpenStatus: StoreOpenStatusType,
) => {
  const status = computeServerStoreStatus(storeOpenStatus)
  return (
    status !== UnknownStoreStatus && Object.values(StoreStatus).includes(status)
  )
}
export const StoreStatusChange: FC = () => {
  const locale = useLocaleContext("locale")
  const statusStrings = useTranslationsMap({
    pausedUntil: "store.busyMode.pausedUntil",
    pauseTimeAsking: "store.busyMode.pauseTimeAsking",
    stopOrdersTemporarily: "store.busyMode.stopOrdersTemporarily",
    prepareOrderTimeAsking: "store.busyMode.prepareOrderTimeAsking",
    adjustTimeText: "store.busyMode.adjustTimeText",
    setStoreStatus: "store.status.setStoreStatus",
    normal: "store.status.normal",
    busy: "store.status.busy",
    paused: "store.status.paused",
    pausedByGrab: "store.status.pausedByGrab",
    closed: "store.status.closed",
    confirm: "store.status.confirm",
    loading: "common.loading",
    minutesTemplate: "store.busyMode.minutesTemplate",
    duration30Mins: "store.busyMode.duration30Mins",
    duration1Hour: "store.busyMode.duration1Hour",
    duration24Hours: "store.busyMode.duration24Hours",
  })
  const {
    pausedUntil: pausedUntilTranslation,
    pauseTimeAsking: pauseTimeAskingTranslation,
    stopOrdersTemporarily: stopOrdersTemporarilyTranslation,
    prepareOrderTimeAsking,
    adjustTimeText,
    setStoreStatus,
    normal: normalLabel,
    busy: busyLabel,
    paused: pausedLabel,
    pausedByGrab: pausedByGrabLabel,
    closed: closedLabel,
    confirm: confirmLabel,
    loading: loadingLabel,
    minutesTemplate,
    duration30Mins,
    duration1Hour,
    duration24Hours,
  } = statusStrings

  const pauseDurationGroup = React.useMemo(
    () => [
      { value: 30, text: duration30Mins },
      { value: 60, text: duration1Hour },
      { value: 1440, text: duration24Hours },
    ],
    [duration30Mins, duration1Hour, duration24Hours],
  )
  const {
    orderManagerMerchant: {
      storeOpenStatus,
      maxAPIMerchantID,
      isUpdatingStoreStatus,
      maxAPIStore: {
        merchant: {
          busyModeConfig = {
            busyModeFptLowerBound: 0,
            busyModeFptUpperBound: 0,
            busyModeApproach: 0,
          },
          autoAcceptGroup = "",
          specialMerchantType = "",
          featureFlagMapping = {},
        } = {},
      } = {},
    },
  } = useSelector((state: AppState) => state)

  const {
    flags: {
      updateStoreStatus: isRtkUpdateStoreStatusEnabled,
      fetchStoreOpenStatus: isRtkFetchStoreOpenStatusEnabled,
    },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()
  const [updateStoreStatusMutation] = useUpdateStoreStatusMutation()
  const [fetchStoreOpenStatusRtk] = useLazyFetchStoreOpenStatusQuery()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedStoreStatus, setSelectedStoreStatus] = useState<StoreStatus>()
  const [pauseDuration, setPauseDuration] = useState(30)
  const dispatch = useDispatch()
  const [busyModeDuration, setBusyModeDuration] = useState(
    busyModeConfig.busyModeFptLowerBound,
  )
  useEffect(() => {
    let intervalID: NodeJS.Timeout
    if (maxAPIMerchantID && !isRtkFlagLoading) {
      const doFetch = async () => {
        if (isRtkFetchStoreOpenStatusEnabled) {
          const result = await fetchStoreOpenStatusRtk({
            merchantID: maxAPIMerchantID,
          })
          if (result.data) {
            dispatch({
              type: getActionSuccess(actions.FETCH_STORE_OPEN_STATUS),
              payload: { data: result.data },
              meta: {
                previousAction: {
                  type: actions.FETCH_STORE_OPEN_STATUS,
                  payload: {
                    request: { headers: { merchantID: maxAPIMerchantID } },
                  },
                },
              },
            })
          }
          TroyWebTracker.sendEvent({
            category: EventCategoryEng.rtkMigration,
            action: EventActionEng.fetchStoreOpenStatus,
            labels: {
              MERCHANT_ID: maxAPIMerchantID,
              IS_RTK_ENABLED: isRtkFetchStoreOpenStatusEnabled,
              FROM_RTK_QUERY: true,
              FROM_OLD_REDUX: false,
              ACTION_NAME: "fetchStoreOpenStatus",
            },
          })
        } else {
          dispatch(
            actionCreator.orderManagerMerchant.fetchStoreOpenStatus({
              merchantID: maxAPIMerchantID,
            }),
          )
          TroyWebTracker.sendEvent({
            category: EventCategoryEng.rtkMigration,
            action: EventActionEng.fetchStoreOpenStatus,
            labels: {
              MERCHANT_ID: maxAPIMerchantID,
              IS_RTK_ENABLED: isRtkFetchStoreOpenStatusEnabled,
              FROM_RTK_QUERY: false,
              FROM_OLD_REDUX: true,
              ACTION_NAME: "fetchStoreOpenStatus",
            },
          })
        }
      }

      doFetch()
      intervalID = setInterval(doFetch, STORE_STATUS_POLL_INTERVAL_MS)
    }
    return () => {
      if (intervalID) clearInterval(intervalID)
    }
  }, [
    dispatch,
    maxAPIMerchantID,
    isRtkFlagLoading,
    isRtkFetchStoreOpenStatusEnabled,
  ])
  useEffect(() => {
    if (storeOpenStatus) {
      const status = computeServerStoreStatus(storeOpenStatus)
      if (status !== UnknownStoreStatus) setSelectedStoreStatus(status)
    }
  }, [storeOpenStatus])
  useEffect(() => {
    if (!isUpdatingStoreStatus) {
      setIsModalOpen(false)
      if (maxAPIMerchantID && !isRtkFlagLoading) {
        if (isRtkFetchStoreOpenStatusEnabled) {
          fetchStoreOpenStatusRtk({ merchantID: maxAPIMerchantID }).then(
            result => {
              if (result.data) {
                dispatch({
                  type: getActionSuccess(actions.FETCH_STORE_OPEN_STATUS),
                  payload: { data: result.data },
                  meta: {
                    previousAction: {
                      type: actions.FETCH_STORE_OPEN_STATUS,
                      payload: {
                        request: { headers: { merchantID: maxAPIMerchantID } },
                      },
                    },
                  },
                })
              }
            },
          )
        } else {
          dispatch(
            actionCreator.orderManagerMerchant.fetchStoreOpenStatus({
              merchantID: maxAPIMerchantID,
            }),
          )
        }
      }
    }
  }, [
    dispatch,
    isUpdatingStoreStatus,
    maxAPIMerchantID,
    isRtkFlagLoading,
    isRtkFetchStoreOpenStatusEnabled,
  ])
  const serverStoreStatus =
    storeOpenStatus && computeServerStoreStatus(storeOpenStatus)

  useEffect(() => {
    TroyWebTracker.sendEvent({
      category: EventCategory.changeStoreStatus,
      action: EventAction.showChangeStoreStatusButton,
    })
  }, [])

  const changeStoreStatus = (status: StoreStatus) => {
    setSelectedStoreStatus(status)
  }
  const isOpsPaused =
    storeOpenStatus?.closeReason === storeCloseReasons.OpsPaused ||
    storeOpenStatus?.closeReason === storeCloseReasons.OpsPausedWithoutComm

  const getStoreStatusLabel = (status: StoreStatus) => {
    switch (status) {
      case StoreStatus.normal:
        return normalLabel
      case StoreStatus.busy:
        return busyLabel
      case StoreStatus.paused:
        return isOpsPaused ? pausedByGrabLabel : pausedLabel
      case StoreStatus.closed:
        return closedLabel
      default:
        return status
    }
  }

  const convertToEndpointParam = (
    storeStatus: StoreStatus,
  ): StoreStatusForEndpointParam => {
    switch (storeStatus) {
      case StoreStatus.normal:
        return StoreStatusForEndpointParam.normal
      case StoreStatus.paused:
        if (isOpsPaused) {
          return StoreStatusForEndpointParam.opsPaused
        }
        return StoreStatusForEndpointParam.paused
      case StoreStatus.busy:
        return StoreStatusForEndpointParam.busy
      default:
        return StoreStatusForEndpointParam.normal
    }
  }
  const generateTempPauseEnd = (minutes: number): string => {
    const tempPauseEndDate = new Date()
    tempPauseEndDate.setMinutes(tempPauseEndDate.getMinutes() + minutes)
    return tempPauseEndDate.toISOString()
  }
  const handleConfirmClick = async () => {
    if (
      !maxAPIMerchantID ||
      serverStoreStatus === UnknownStoreStatus ||
      serverStoreStatus === undefined ||
      selectedStoreStatus === undefined
    ) {
      return
    }
    let params: UpdateStoreStatusParams = {
      fromState: convertToEndpointParam(serverStoreStatus),
      toState: convertToEndpointParam(selectedStoreStatus),
      busyModeRequest: {},
      tempPauseRequest: {},
    }
    switch (`${serverStoreStatus}:${selectedStoreStatus}`) {
      case `${StoreStatus.normal}:${StoreStatus.busy}`:
      case `${StoreStatus.paused}:${StoreStatus.busy}`:
      case `${StoreStatus.busy}:${StoreStatus.busy}`:
        params.busyModeRequest = {
          busyModeFoodPrepareTime: busyModeDuration,
          busyModeStartTime: null,
        }
        break
      case `${StoreStatus.normal}:${StoreStatus.paused}`:
      case `${StoreStatus.busy}:${StoreStatus.paused}`:
        params.tempPauseRequest = {
          isUnpause: false,
          tempPauseEnd: generateTempPauseEnd(pauseDuration),
        }
        break
      case `${StoreStatus.paused}:${StoreStatus.normal}`:
        params.tempPauseRequest = {
          isUnpause: true,
          tempPauseEnd: null,
        }
        break
      case `${StoreStatus.busy}:${StoreStatus.normal}`:
        params.busyModeRequest = {
          busyModeFoodPrepareTime: 0,
          busyModeStartTime: null,
        }
        break
      default:
        params = {
          fromState: convertToEndpointParam(serverStoreStatus),
          toState: convertToEndpointParam(selectedStoreStatus),
          busyModeRequest: {},
          tempPauseRequest: {},
        }
    }

    if (!isRtkFlagLoading && isRtkUpdateStoreStatusEnabled) {
      dispatch({ type: actions.UPDATE_STORE_STATUS })

      try {
        const result = await updateStoreStatusMutation({
          merchantID: maxAPIMerchantID,
          params,
        })

        if (result.error) {
          dispatch({
            type: getActionFail(actions.UPDATE_STORE_STATUS),
            payload: { data: {} },
            meta: {
              previousAction: {
                type: actions.UPDATE_STORE_STATUS,
                payload: { request: { data: params } },
              },
            },
          })
        } else {
          dispatch({
            type: getActionSuccess(actions.UPDATE_STORE_STATUS),
            payload: {
              data: result.data,
            },
            meta: {
              previousAction: {
                type: actions.UPDATE_STORE_STATUS,
                payload: { request: { data: params } },
              },
            },
          })
        }
      } catch {
        dispatch({
          type: getActionFail(actions.UPDATE_STORE_STATUS),
          payload: { data: {} },
          meta: {
            previousAction: {
              type: actions.UPDATE_STORE_STATUS,
              payload: { request: { data: params } },
            },
          },
        })
      }

      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.updateStoreStatus,
        labels: {
          MERCHANT_ID: maxAPIMerchantID,
          IS_RTK_ENABLED: isRtkUpdateStoreStatusEnabled,
          FROM_RTK_QUERY: true,
          FROM_OLD_REDUX: false,
          ACTION_NAME: "updateStoreStatus",
        },
      })
    } else if (!isRtkFlagLoading) {
      dispatch(
        actionCreator.orderManagerMerchant.updateStoreStatus({
          merchantID: maxAPIMerchantID,
          params,
        }),
      )

      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.updateStoreStatus,
        labels: {
          MERCHANT_ID: maxAPIMerchantID,
          IS_RTK_ENABLED: isRtkUpdateStoreStatusEnabled,
          FROM_RTK_QUERY: false,
          FROM_OLD_REDUX: true,
          ACTION_NAME: "updateStoreStatus",
        },
      })
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.changeStoreStatus,
      action: EventAction.submitChangeStoreStatus,
      labels: params,
    })
  }

  const onPauseDurationChange = (e: RadioChangeEvent) => {
    setPauseDuration(e.target.value)
  }

  const handlePopoverChange = (
    isOpen: boolean | ((prevState: boolean) => boolean),
  ) => {
    if (isOpen) {
      let busyModeDurationValue: number | undefined

      if (isBusyModeAllowed && busyModeConfig.busyModeFptLowerBound) {
        busyModeDurationValue = busyModeConfig.busyModeFptLowerBound
      }
      if (storeOpenStatus) {
        const status = computeServerStoreStatus(storeOpenStatus)
        if (status !== UnknownStoreStatus) {
          setSelectedStoreStatus(status)
        }

        if (isBusyModeAllowed && status === StoreStatus.busy) {
          busyModeDurationValue =
            storeOpenStatus.meta?.currentBusyModePrepTime ?? 0
        }
      }
      if (busyModeDurationValue !== undefined) {
        setBusyModeDuration(busyModeDurationValue)
      }
    }
    setIsModalOpen(isOpen)
  }

  const subText = React.useMemo(() => {
    if (selectedStoreStatus === StoreStatus.paused) {
      if (
        serverStoreStatus === StoreStatus.paused &&
        storeOpenStatus?.meta.pauseEndingTime
      ) {
        const reOpenTime = formatPauseEndingTime(
          storeOpenStatus?.meta.pauseEndingTime,
          locale,
        )
        return `${pausedUntilTranslation} ${reOpenTime}`
      }
      return `${pauseTimeAskingTranslation}`
    }
    return `${stopOrdersTemporarilyTranslation}`
  }, [
    selectedStoreStatus,
    serverStoreStatus,
    storeOpenStatus,
    pausedUntilTranslation,
    pauseTimeAskingTranslation,
    stopOrdersTemporarilyTranslation,
    locale,
  ])

  const isBusyModeAllowed =
    !!featureFlagMapping?.IsBusyModeSupported &&
    autoAcceptGroup !== AUTO_ACCEPT_GROUP.AutoAcceptGroupC &&
    specialMerchantType !== SPECIAL_MERCHANT_TYPE.mixAndMatchType &&
    busyModeConfig.busyModeFptUpperBound > 0 &&
    busyModeConfig.busyModeFptLowerBound < busyModeConfig.busyModeFptUpperBound

  useEffect(() => {
    if (isBusyModeAllowed) {
      TroyWebTracker.sendEvent({
        category: EventCategory.changeStoreStatus,
        action: EventAction.BusyOptionShown,
      })
    }
  }, [isBusyModeAllowed])

  const content = (
    <div css={customPopoverStyles}>
      <div css={modalTitle}>{setStoreStatus}</div>
      <div
        css={statusSection}
        onClick={() => {
          changeStoreStatus(StoreStatus.normal)
        }}
      >
        <div css={dotWrapper}>
          <PlaceholderFilled className="green-dot" />
        </div>
        <div css={statusMiddleSection}>
          <span css={statusText}>{normalLabel}</span>
        </div>
        <TickMarkFilled
          css={tickMarkIcon}
          style={{
            visibility:
              selectedStoreStatus === StoreStatus.normal ? "visible" : "hidden",
          }}
        />
      </div>
      <Divider css={dividerStyle} />
      {isBusyModeAllowed && (
        <Fragment>
          <div>
            <div
              css={statusSection}
              onClick={() => {
                changeStoreStatus(StoreStatus.busy)
              }}
            >
              <div css={dotWrapper}>
                <PlaceholderFilled className="orange-dot" />
              </div>
              <div css={statusMiddleSection}>
                <span css={statusText}>{busyLabel}</span>
                {selectedStoreStatus === StoreStatus.busy ? (
                  <p css={statusSubText}>{prepareOrderTimeAsking}</p>
                ) : (
                  <p css={statusSubText}>{adjustTimeText}</p>
                )}
              </div>
              <TickMarkFilled
                css={tickMarkIcon}
                style={{
                  visibility:
                    selectedStoreStatus === StoreStatus.busy
                      ? "visible"
                      : "hidden",
                }}
              />
            </div>

            {selectedStoreStatus === StoreStatus.busy && (
              <TimePicker
                setBusyModeDuration={setBusyModeDuration}
                busyModeDuration={busyModeDuration}
                busyModeConfig={busyModeConfig}
                minutesTemplate={minutesTemplate}
              />
            )}
          </div>
          <Divider css={dividerStyle} />
        </Fragment>
      )}
      <div>
        <div
          css={statusSection}
          onClick={() => {
            changeStoreStatus(StoreStatus.paused)
          }}
        >
          <div css={dotWrapper}>
            <PlaceholderFilled className="red-dot" />
          </div>
          <div css={statusMiddleSection}>
            <span css={statusText}>
              {isOpsPaused ? pausedByGrabLabel : pausedLabel}
            </span>
            <p css={statusSubText}>{subText}</p>
          </div>
          <TickMarkFilled
            css={tickMarkIcon}
            style={{
              visibility:
                selectedStoreStatus === StoreStatus.paused
                  ? "visible"
                  : "hidden",
            }}
          />
        </div>
        {selectedStoreStatus === StoreStatus.paused &&
          serverStoreStatus !== StoreStatus.paused && (
            <Radio.Group
              value={pauseDuration}
              onChange={onPauseDurationChange}
              css={radioGroup}
              buttonStyle="solid"
            >
              {pauseDurationGroup.map(item => (
                <Radio.Button css={radioButton} value={item.value}>
                  {item.text}
                </Radio.Button>
              ))}
            </Radio.Group>
          )}
      </div>

      <Divider css={dividerStyle} />
      <Button
        css={confirmButton}
        onClick={handleConfirmClick}
        disabled={
          selectedStoreStatus === serverStoreStatus &&
          selectedStoreStatus === StoreStatus.paused
        }
      >
        {isUpdatingStoreStatus ? loadingLabel : confirmLabel}
      </Button>
    </div>
  )

  return storeOpenStatus &&
    shouldShowChangeStoreStatusButton(storeOpenStatus) &&
    serverStoreStatus !== undefined &&
    serverStoreStatus !== UnknownStoreStatus ? (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      open={isModalOpen}
      onOpenChange={handlePopoverChange}
    >
      <Button
        onClick={() => {
          if (!isModalOpen) {
            TroyWebTracker.sendEvent({
              category: EventCategory.changeStoreStatus,
              action: EventAction.clickButtonThenModalOpened,
            })
          }
          setIsModalOpen(!isModalOpen)
        }}
        css={[buttonBase, buttonStyle[serverStoreStatus.toLowerCase()]]}
        data-testid="store-status-button"
        disabled={serverStoreStatus === StoreStatus.closed}
      >
        {getStoreStatusLabel(serverStoreStatus)}{" "}
        {serverStoreStatus !== StoreStatus.closed &&
          (isModalOpen ? <ChevronTopUniversal /> : <ChevronDownUniversal />)}
      </Button>
    </Popover>
  ) : null
}
